import { useCallback, useContext, useMemo } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

export default function useKnowledgeBase() {
  const authCtx = useContext(AuthContext);
  const { sendRequest, isLoading, error, setError } = useHttp();

  const buildHeaders = useCallback(() => authCtx.getAuthHeaders(), [authCtx]);

  const listArticles = useCallback(
    async ({ status, search, limit = 50, skip = 0 } = {}) => {
      const params = {};
      if (status && status !== 'all') params.status = status;
      if (search) params.search = search;
      params.limit = limit;
      params.skip = skip;

      return await sendRequest({
        url: '/api/v1/knowledge-base',
        method: 'GET',
        headers: buildHeaders(),
        params,
      });
    },
    [buildHeaders, sendRequest]
  );

  const getArticle = useCallback(
    async articleId => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/${articleId}`,
        method: 'GET',
        headers: buildHeaders(),
      });
    },
    [buildHeaders, sendRequest]
  );

  const createArticle = useCallback(
    async payload => {
      return await sendRequest({
        url: '/api/v1/knowledge-base',
        method: 'POST',
        headers: buildHeaders(),
        body: payload,
      });
    },
    [buildHeaders, sendRequest]
  );

  const updateArticle = useCallback(
    async (articleId, payload) => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/${articleId}`,
        method: 'PATCH',
        headers: buildHeaders(),
        body: payload,
      });
    },
    [buildHeaders, sendRequest]
  );

  const publishArticle = useCallback(
    async articleId => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/${articleId}/publish`,
        method: 'POST',
        headers: buildHeaders(),
      });
    },
    [buildHeaders, sendRequest]
  );

  const archiveArticle = useCallback(
    async articleId => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/${articleId}/archive`,
        method: 'POST',
        headers: buildHeaders(),
      });
    },
    [buildHeaders, sendRequest]
  );

  const generateArticleFromUrl = useCallback(
    async ({ url } = {}) => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/generate-from-url`,
        method: 'POST',
        headers: buildHeaders(),
        body: { url },
      });
    },
    [buildHeaders, sendRequest]
  );

  const generateTags = useCallback(
    async ({ title, contentMarkdown } = {}) => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/generate-tags`,
        method: 'POST',
        headers: buildHeaders(),
        body: { title, contentMarkdown },
      });
    },
    [buildHeaders, sendRequest]
  );

  const deleteArticle = useCallback(
    async articleId => {
      return await sendRequest({
        url: `/api/v1/knowledge-base/${articleId}`,
        method: 'DELETE',
        headers: buildHeaders(),
      });
    },
    [buildHeaders, sendRequest]
  );

  return useMemo(
    () => ({
      isLoading,
      error,
      setError,
      listArticles,
      getArticle,
      createArticle,
      updateArticle,
      publishArticle,
      archiveArticle,
      generateArticleFromUrl,
      generateTags,
      deleteArticle,
    }),
    [
      archiveArticle,
      createArticle,
      deleteArticle,
      error,
      getArticle,
      isLoading,
      listArticles,
      publishArticle,
      setError,
      updateArticle,
      generateArticleFromUrl,
      generateTags,
    ]
  );
}
